/**
 * Thin, typed Apollo wrappers for the three INSi GraphQL operations.
 *
 * The gql documents live in the feature `gqlSchemes/` folder (mirroring the
 * existing `createPatient.js` pattern). These hooks only add §4 typing on top
 * of Apollo's `useMutation` / `useLazyQuery`.
 */
import {
  useMutation,
  useLazyQuery,
  MutationHookOptions,
  LazyQueryHookOptions,
} from '@apollo/client';

import { SEARCH_INS_BY_TRAITS } from 'pages/PatientsPage/gqlSchemes/searchInsByTraits';
import { QUALIFY_PATIENT_IDENTITY } from 'pages/PatientsPage/gqlSchemes/qualifyPatientIdentity';
import { FIND_PATIENT_BY_INS } from 'pages/PatientsPage/gqlSchemes/findPatientByIns';

import {
  SearchInsByTraitsInput,
  SearchInsByTraitsResult,
  InsType,
  InsIdentifierInput,
  IdentityDocumentAttestationInput,
  QualifyPatientIdentityResult,
  PatientSummary,
} from './types';

/** Standard mutation payload envelope ({ ok, error, result }). */
export interface GqlMutationError {
  code: string;
  message: string;
  params?: Record<string, unknown> | null;
}

export interface MutationPayload<TResult> {
  ok: boolean;
  error: GqlMutationError | null;
  result: TResult | null;
}

// ---- searchInsByTraits ----

export interface SearchInsByTraitsData {
  insi: {
    searchInsByTraits: MutationPayload<SearchInsByTraitsResult>;
  };
}

export interface SearchInsByTraitsVars {
  record: SearchInsByTraitsInput;
}

export const useSearchInsByTraits = (
  options?: MutationHookOptions<SearchInsByTraitsData, SearchInsByTraitsVars>
) =>
  useMutation<SearchInsByTraitsData, SearchInsByTraitsVars>(
    SEARCH_INS_BY_TRAITS,
    options
  );

// ---- qualifyPatientIdentity ----

export interface QualifyPatientIdentityInput {
  patientId: string;
  document: IdentityDocumentAttestationInput;
}

export interface QualifyPatientIdentityData {
  insi: {
    qualifyPatientIdentity: MutationPayload<QualifyPatientIdentityResult>;
  };
}

export interface QualifyPatientIdentityVars {
  record: QualifyPatientIdentityInput;
}

export const useQualifyPatientIdentity = (
  options?: MutationHookOptions<
    QualifyPatientIdentityData,
    QualifyPatientIdentityVars
  >
) =>
  useMutation<QualifyPatientIdentityData, QualifyPatientIdentityVars>(
    QUALIFY_PATIENT_IDENTITY,
    options
  );

// ---- findPatientByIns (lazy — triggered on demand) ----

export interface FindPatientByInsData {
  insi: {
    findPatientByIns: PatientSummary | null;
  };
}

export interface FindPatientByInsVars {
  insValue: string;
  insType: InsType;
}

export const useFindPatientByIns = (
  options?: LazyQueryHookOptions<FindPatientByInsData, FindPatientByInsVars>
) =>
  useLazyQuery<FindPatientByInsData, FindPatientByInsVars>(
    FIND_PATIENT_BY_INS,
    options
  );

// Re-export the input type used at create/update call sites for convenience.
export type { InsIdentifierInput };
